<template>
    <!-- Non-modal: the panel pushes the page aside instead of covering it, so there is no focus trap -->
    <div
        class="side-panel"
        role="dialog"
        :aria-labelledby="labelledBy"
        @keydown.esc="onEscapeKeydown">
        <div class="header-wrapper">
            <div class="header-wrapper-inner">
                <pendo-button
                    ref="closeButton"
                    theme="app"
                    icon="x"
                    icon-size="20"
                    type="tertiary"
                    class="close-icon"
                    :aria-label="$t('common.Close')"
                    @click="closeSidePanel" />
                <div
                    :id="headerId"
                    class="header-slot-wrapper">
                    <slot
                        name="header"
                        class="header-slot" />
                </div>
            </div>
        </div>
        <slot
            name="body"
            style="height: 100%" />
    </div>
</template>

<script>
import { PendoButton } from '@pendo/components-vue3';
import uniqueId from 'lodash/uniqueId';
import { EventBus } from '@utils/event-bus.js';
import { getFirstFocusableNode } from '@/utils/dom.utils';

export default {
    name: 'SidePanel',
    components: {
        PendoButton,
    },
    // Header content arrives as prebuilt vnodes, so a title opts in to naming the dialog through inject
    provide () {
        return {
            sidePanelTitle: {
                id: this.titleId,
                register: () => this.registerTitle(),
                unregister: () => this.unregisterTitle(),
            },
        };
    },
    data () {
        return {
            headerId: uniqueId('side-panel-header-'),
            titleId: uniqueId('side-panel-title-'),
            titleCount: 0,
        };
    },
    computed: {
        labelledBy () {
            return this.titleCount > 0 ? this.titleId : this.headerId;
        },
    },
    created () {
        EventBus.$on('focusSidePanel', this.focusPanel);
    },
    beforeUnmount () {
        EventBus.$off('focusSidePanel', this.focusPanel);
    },
    methods: {
        focusPanel () {
            const node = this.$el && getFirstFocusableNode(this.$el);
            if (node) {
                node.focus();
            } else {
                const btn = this.$refs.closeButton;
                const btnEl = btn?.$el ?? btn;
                const focusableInBtn = btnEl && getFirstFocusableNode(btnEl);
                if (focusableInBtn) {
                    focusableInBtn.focus();
                }
            }
        },
        closeSidePanel () {
            EventBus.$emit('sidePanelClosed');
        },
        onEscapeKeydown (event) {
            // Some popups, like the date picker, close on Escape without preventing default; their trigger still reads expanded while it bubbles
            if (event.defaultPrevented || event.target?.closest?.('[aria-expanded="true"]')) {
                return;
            }

            this.closeSidePanel();
        },
        registerTitle () {
            this.titleCount += 1;
        },
        unregisterTitle () {
            this.titleCount -= 1;
        },
    },
};
</script>

<style lang="less" scoped>
.side-panel {
    width: 100%;
    height: 100%;
    border-left: solid 1px var(--container-border-base);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    position: relative;

    .header-wrapper {
        padding: 20px 16px;

        .header-wrapper-inner {
            display: flex;
            align-items: flex-start;
            gap: 8px;
        }

        .header-slot-wrapper {
            flex: 1 1 auto;
            min-width: 0;
            word-wrap: break-word;
        }

        .close-icon {
            color: var(--text-body-subtle-base);
            height: 24px;
            width: 24px;
            order: 2;
            flex: 0 0 auto;

            &:hover {
                color: var(--text-body-subtle-hover);
            }
        }
    }
}
</style>
